import React, { createContext, useContext, useMemo, useReducer } from "react";

import {
  Audience,
  AudienceSegment,
  AudienceTravelTime,
  GeoFenceLocation,
  SiteCatchment,
  ValidFile,
  Variable,
} from "@/types";
import * as T from "./actionTypes";
import { audienceReducer } from "./AudienceReducer";
import { AudienceAction, AudienceState, BuildResult, initialState } from "./types";

/** Everything that changes the audience being edited, bound to the reducer */
export const bindActions = (dispatch: React.Dispatch<AudienceAction>) => ({
  // ---- The audience being edited ----
  /** Starts editing an audience, e.g. a saved one or a new default */
  load: (audience: Audience | null) => dispatch({ type: T.LOAD_AUDIENCE, payload: audience }),
  /** Clears the audience so the new audience page starts afresh */
  create: () => dispatch({ type: T.CREATE_AUDIENCE }),
  /** The audience has been saved, so it no longer counts as changed */
  saved: (audience: Audience) => dispatch({ type: T.SAVED_AUDIENCE, payload: audience }),
  built: (result: BuildResult) => dispatch({ type: T.BUILD_AUDIENCE, payload: result }),
  reset: () => dispatch({ type: T.RESET }),

  addSegments: (segments: AudienceSegment[]) => dispatch({ type: T.ADD_SEGMENTS, payload: segments }),
  selectSegment: (segment: AudienceSegment) => dispatch({ type: T.CHANGE_SEGMENT, payload: segment }),
  renamed: (audienceId: string, audienceName: string, projectName: string | null) =>
    dispatch({ type: T.CHANGE_NAME, payload: { audienceId, audienceName, projectName } }),
  shared: (audienceId: string, userIds: number[]) =>
    dispatch({ type: T.SHARE_AUDIENCE, payload: { audienceId, userIds } }),

  // ---- Build audiences ----
  addGroup: () => dispatch({ type: T.ADD_VARIABLE_GROUP }),
  removeGroup: (groupId: number) => dispatch({ type: T.REMOVE_VARIABLE_GROUP, payload: groupId }),
  resetGroup: (groupId: number) => dispatch({ type: T.RESET_VARIABLE_GROUP, payload: groupId }),
  setOperator: (operator: string) => dispatch({ type: T.CHANGE_OPERATOR, payload: operator }),
  setGroupOperator: (groupId: number, operator: string) =>
    dispatch({ type: T.CHANGE_GROUP_OPERATOR, payload: { id: groupId, operator } }),
  setGroupVariables: (groupId: number, variables: Variable[]) =>
    dispatch({ type: T.CHANGE_GROUP_VARIABLES, payload: { id: groupId, variables } }),
  addVariables: (groupId: number, variables: Variable[]) =>
    dispatch({ type: T.ADD_VARIABLES, payload: { id: groupId, variables } }),
  removeVariable: (groupId: number, tagId: number) =>
    dispatch({ type: T.REMOVE_VARIABLE, payload: { id: groupId, tagId } }),
  setBuildMode: (mode: string) => dispatch({ type: T.CHANGE_BUILD_MODE, payload: mode }),
  setGeoFence: (locations: GeoFenceLocation[]) => dispatch({ type: T.SET_GEO_FENCE, payload: locations }),

  // ---- Upload audiences ----
  setUploadFile: (file: File | null) =>
    dispatch(file ? { type: T.ADD_UPLOAD_FILE, payload: file } : { type: T.CLEAR_UPLOAD_FILE }),
  setUploadMode: (mode: string) => dispatch({ type: T.CHANGE_UPLOAD_MODE, payload: mode }),
  changeTravelTime: (changes: Partial<AudienceTravelTime>) => dispatch({ type: T.CHANGE_TRAVELTIME, payload: changes }),
  setFileCheck: (result: ValidFile | null) => dispatch({ type: T.CHECK_UPLOAD_FILE, payload: result }),

  // ---- Site audiences ----
  setSites: (siteIds: number[]) => dispatch({ type: T.SET_SITES, payload: siteIds }),
  setCatchment: (catchment: SiteCatchment[]) => dispatch({ type: T.SET_SITE_CATCHMENT, payload: catchment }),
  setSiteCategories: (categories: string[]) => dispatch({ type: T.SET_SITE_CATEGORIES, payload: categories }),
  setSiteDayType: (dayType: string) => dispatch({ type: T.SET_SITE_DAY_TYPE, payload: dayType }),
});

export type AudienceActions = ReturnType<typeof bindActions>;

// State and actions are separate so components that only change the audience
// don't re-render when it changes
const AudienceStateContext = createContext<AudienceState | undefined>(undefined);
const AudienceActionsContext = createContext<AudienceActions | undefined>(undefined);

export const AudienceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(audienceReducer, initialState);

  // dispatch never changes, so neither do the actions
  const actions = useMemo(() => bindActions(dispatch), [dispatch]);

  return (
    <AudienceActionsContext.Provider value={actions}>
      <AudienceStateContext.Provider value={state}>
        {children}
      </AudienceStateContext.Provider>
    </AudienceActionsContext.Provider>
  );
};

/** The audience being edited. Re-renders whenever it changes. */
export const useAudienceContext = () => {
  const state = useContext(AudienceStateContext);
  if (state === undefined) {
    throw new Error("useAudienceContext must be used within an AudienceProvider");
  }
  return state;
};

/** Actions that change the audience being edited. Never changes identity. */
export const useAudienceActions = () => {
  const actions = useContext(AudienceActionsContext);
  if (actions === undefined) {
    throw new Error("useAudienceActions must be used within an AudienceProvider");
  }
  return actions;
};
